import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Link2, Eye, TrendingUp, Calendar, CheckCircle,
  Clock, Building2, RefreshCw, Filter, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminLandingsPanel = () => {
  const [landings, setLandings] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState({});
  const [appointmentStats, setAppointmentStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [filter, setFilter] = useState({ status: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [landingsRes, appointmentsRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/landings/admin/all${filter.status ? `?status=${filter.status}` : ''}`),
        axios.get(`${API_URL}/api/landings/admin/appointments`),
        axios.get(`${API_URL}/api/landings/admin/analytics?days=30`)
      ]);
      
      setLandings(landingsRes.data.landings || []);
      setStats(landingsRes.data.stats || {});
      setAppointments(appointmentsRes.data.appointments || []);
      setAppointmentStats(appointmentsRes.data.stats || {});
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Link2 className="w-7 h-7 text-emerald-400" />
            Landing Pages & Citas
          </h2>
          <p className="text-gray-400 mt-1">
            Métricas de conversión sin cupones
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 mb-2">
            <Link2 className="w-4 h-4" />
            <span className="text-sm">Landings</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
          <p className="text-xs text-emerald-400">{stats.active || 0} activas</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 mb-2">
            <Eye className="w-4 h-4" />
            <span className="text-sm">Visitas</span>
          </div>
          <p className="text-2xl font-bold text-white">{analytics?.total_views || 0}</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Conversiones</span>
          </div>
          <p className="text-2xl font-bold text-white">{analytics?.total_conversions || 0}</p>
          <p className="text-xs text-emerald-400">{analytics?.conversion_rate || 0}% ratio</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 mb-2">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Citas</span>
          </div>
          <p className="text-2xl font-bold text-white">{appointmentStats.total || 0}</p>
          <p className="text-xs text-amber-400">{appointmentStats.pending || 0} pendientes</p>
        </div>

        <div className="bg-emerald-900/30 rounded-xl p-4 border border-emerald-700">
          <div className="flex items-center gap-2 text-emerald-400 mb-2">
            <BarChart3 className="w-4 h-4" />
            <span className="text-sm">Valor Total</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{formatCurrency(stats.total_value)}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {[
          { id: 'overview', label: 'Resumen' },
          { id: 'landings', label: `Landings (${landings.length})` },
          { id: 'appointments', label: `Citas (${appointmentStats.pending || 0})` }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id 
                ? 'border-emerald-500 text-emerald-400' 
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Landings */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Top Landings por Conversiones</h3>
            <div className="space-y-3">
              {landings
                .sort((a, b) => (b.metrics?.conversions || 0) - (a.metrics?.conversions || 0))
                .slice(0, 5)
                .map((landing, idx) => (
                  <div key={landing.landing_id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{idx + 1}</span>
                      <div>
                        <p className="text-white font-medium">{landing.title}</p>
                        <p className="text-xs text-gray-400">{landing.business_data?.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 font-semibold">{landing.metrics?.conversions || 0}</p>
                      <p className="text-xs text-gray-500">{landing.metrics?.views || 0} visitas</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Recent Appointments */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Citas Recientes</h3>
            <div className="space-y-3">
              {appointments.slice(0, 5).map(apt => (
                <div key={apt.appointment_id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      apt.status === 'pending' ? 'bg-amber-500/20' :
                      apt.status === 'confirmed' ? 'bg-blue-500/20' :
                      apt.status === 'completed' ? 'bg-emerald-500/20' : 'bg-gray-500/20'
                    }`}>
                      {apt.status === 'pending' ? <Clock className="w-4 h-4 text-amber-400" /> :
                       apt.status === 'confirmed' ? <Calendar className="w-4 h-4 text-blue-400" /> :
                       <CheckCircle className="w-4 h-4 text-emerald-400" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{apt.customer_name}</p>
                      <p className="text-xs text-gray-400">{apt.business_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-300">
                      {new Date(apt.datetime).toLocaleDateString('es-ES')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(apt.datetime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Landings Tab */}
      {activeTab === 'landings' && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-gray-700 flex items-center gap-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filter.status}
              onChange={(e) => setFilter({ status: e.target.value })}
              className="px-3 py-1.5 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm"
            >
              <option value="">Todos</option>
              <option value="active">Activas</option>
              <option value="paused">Pausadas</option>
            </select>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-300">Landing</th>
                  <th className="text-left p-4 font-medium text-gray-300">Negocio</th>
                  <th className="text-left p-4 font-medium text-gray-300">Estado</th>
                  <th className="text-right p-4 font-medium text-gray-300">Visitas</th>
                  <th className="text-right p-4 font-medium text-gray-300">Conversiones</th>
                  <th className="text-right p-4 font-medium text-gray-300">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {landings.map(landing => (
                  <tr key={landing.landing_id} className="hover:bg-gray-700/50">
                    <td className="p-4">
                      <p className="text-white font-medium">{landing.title}</p>
                      <p className="text-xs text-gray-500">{landing.landing_id}</p>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-300">{landing.business_data?.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        landing.status === 'active' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {landing.status === 'active' ? 'Activa' : 'Pausada'}
                      </span>
                    </td>
                    <td className="p-4 text-right text-white">{landing.metrics?.views || 0}</td>
                    <td className="p-4 text-right text-emerald-400">{landing.metrics?.conversions || 0}</td>
                    <td className="p-4 text-right text-white">{formatCurrency(landing.metrics?.conversion_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Appointments Tab */}
      {activeTab === 'appointments' && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-300">Cliente</th>
                  <th className="text-left p-4 font-medium text-gray-300">Negocio</th>
                  <th className="text-left p-4 font-medium text-gray-300">Fecha/Hora</th>
                  <th className="text-left p-4 font-medium text-gray-300">Estado</th>
                  <th className="text-left p-4 font-medium text-gray-300">Contacto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {appointments.map(apt => (
                  <tr key={apt.appointment_id} className="hover:bg-gray-700/50">
                    <td className="p-4">
                      <p className="text-white font-medium">{apt.customer_name}</p>
                      <p className="text-xs text-gray-500">{apt.offer_title}</p>
                    </td>
                    <td className="p-4 text-gray-300">{apt.business_name}</td>
                    <td className="p-4">
                      <p className="text-white">{new Date(apt.datetime).toLocaleDateString('es-ES')}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(apt.datetime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        apt.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        apt.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                        apt.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {apt.status === 'pending' ? 'Pendiente' :
                         apt.status === 'confirmed' ? 'Confirmada' :
                         apt.status === 'completed' ? 'Completada' :
                         apt.status === 'cancelled' ? 'Cancelada' : apt.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-400">
                      <p>{apt.customer_phone}</p>
                      {apt.customer_email && <p className="text-xs">{apt.customer_email}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
